/* ═══════════════════════════════════════════════════════════════
 * PrivacyThemeContext.ts
 *
 * React context interface for the Privacy Suite design language.
 *
 * Lives in its own file so consumers can import `usePrivacyTheme`
 * without dragging the provider implementation (and its
 * persistence side-effects) into the bundle.
 *
 * The context exposes only what UI code legitimately needs — the
 * current theme tokens + a setter. The persistence key, fallback
 * resolution, and CSS-variable wiring are all internal to the
 * provider.
 * ═══════════════════════════════════════════════════════════════ */

import { createContext } from 'react';
import {
	CLASSIC_THEME,
	DEFAULT_PRIVACY_THEME_ID,
	type PrivacyThemeId,
	type PrivacyThemeTokens,
} from './privacyThemeTokens';

export interface PrivacyThemeContextValue {
	/** Tokens for the currently active theme. */
	readonly theme: PrivacyThemeTokens;
	/** Stable ID for the currently active theme. */
	readonly themeId: PrivacyThemeId;
	/**
	 * Switch to a different theme. Unknown IDs are quietly
	 * coerced to the default so a stale localStorage entry can't
	 * blank the suite.
	 */
	setTheme(id: PrivacyThemeId): void;
}

/**
 * The default value is used when a component reads the context
 * WITHOUT a provider above it. Rather than throwing (which would
 * crash the stand-alone wizards loaded straight via a deep link),
 * we hand back the classic theme as a read-only no-op. Callers
 * that need to know whether they have a "real" provider can check
 * `themeId === DEFAULT_PRIVACY_THEME_ID && setTheme === NOOP_SETTER`.
 */
const NOOP_SETTER = (_id: PrivacyThemeId): void => {
	// Intentional no-op — no provider mounted. We still satisfy
	// the type contract, but the caller's request is discarded.
};

export const PrivacyThemeContext = createContext<PrivacyThemeContextValue>({
	theme: CLASSIC_THEME,
	themeId: DEFAULT_PRIVACY_THEME_ID,
	setTheme: NOOP_SETTER,
});

PrivacyThemeContext.displayName = 'PrivacyThemeContext';
